/**
 * Notification UI Module
 * 
 * Provides toast-style notifications for user feedback.
 * 
 * @module src/ui/notification
 */

const NOTIFICATION_GAP = 12;

function updateNotificationStack() {
    const notifications = Array.from(document.querySelectorAll('.notification'))
        .filter((el) => el.isConnected && el.dataset.exiting !== 'true');
    if (!notifications.length) return;

    const baseBottom = Math.max(...notifications.map((el) => Number(el.dataset.baseBottom || 24)));
    let offset = baseBottom;
    notifications.forEach((el) => {
        el.style.bottom = `${offset}px`;
        offset += el.offsetHeight + NOTIFICATION_GAP;
    });
}

function removeNotification(notification) {
    notification.dataset.exiting = 'true';
    notification.style.animation = 'slideOut 0.3s ease-out';
    setTimeout(() => {
        notification.remove();
        updateNotificationStack();
    }, 300);
}

/**
 * Display a notification toast message
 * 
 * @param {string} message - The message to display
 * @param {'info'|'error'|'success'} [type='info'] - The notification type
 */
export function showNotification(message, type = 'info') {
    const normalizedMessage = String(message ?? '').trim();
    if (!normalizedMessage) return;

    // Deduplicate: skip if an identical message is already visible.
    const existing = document.querySelector(`.notification-${type}`);
    if (existing && existing.textContent === normalizedMessage) return;

    const notification = document.createElement('div');
    notification.className = `notification notification-${type}`;
    notification.dataset.baseBottom = '24';
    notification.style.cssText = `
        position: fixed;
        bottom: 24px;
        right: 24px;
        padding: 16px 24px;
        background: ${type === 'error' ? 'var(--accent-red)' : type === 'success' ? 'var(--accent-green)' : 'var(--accent-blue)'};
        color: white;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        z-index: 10001;
        font-size: 14px;
        font-weight: 500;
        max-width: 400px;
        animation: slideIn 0.3s ease-out;
    `;
    notification.textContent = normalizedMessage;

    document.body.appendChild(notification);
    requestAnimationFrame(updateNotificationStack);

    setTimeout(() => {
        removeNotification(notification);
    }, 4000);
}

/**
 * Display an error notification
 * 
 * @param {string} message - The error message to display
 */
export function showErrorNotification(message) {
    showNotification(message, 'error');
}

/**
 * Display a success notification
 *
 * @param {string} message - The success message to display
 */
export function showSuccessNotification(message) {
    showNotification(message, 'success');
}

/**
 * Display a notification with an action button (e.g. "View" after task creation).
 *
 * @param {string} message - The message to display
 * @param {'info'|'error'|'success'} [type='info'] - The notification type
 * @param {string} actionLabel - Text for the action button
 * @param {() => void} actionCallback - Called when the action button is clicked
 * @param {number} [duration=5000] - Auto-dismiss delay in ms
 */
export function showActionNotification(message, type = 'info', actionLabel, actionCallback, duration = 5000) {
    const existing = document.querySelector(`.notification-${type}`);
    if (existing && existing.textContent.includes(message)) existing.remove();

    const notification = document.createElement('div');
    notification.className = `notification notification-${type}`;

    const isMobile = window.innerWidth <= 768;
    notification.dataset.baseBottom = isMobile ? '80' : '24';
    notification.style.cssText = `
        position: fixed;
        bottom: ${isMobile ? '80px' : '24px'};
        ${isMobile ? 'left: 16px; right: 16px; max-width: none;' : 'right: 24px; max-width: 400px;'}
        padding: 16px 24px;
        background: ${type === 'error' ? 'var(--accent-red)' : type === 'success' ? 'var(--accent-green)' : 'var(--accent-blue)'};
        color: white;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        z-index: 10001;
        font-size: 14px;
        font-weight: 500;
        display: flex;
        align-items: center;
        justify-content: space-between;
        animation: slideIn 0.3s ease-out;
    `;

    const msgSpan = document.createElement('span');
    msgSpan.textContent = message;
    notification.appendChild(msgSpan);

    if (actionLabel && actionCallback) {
        const btn = document.createElement('button');
        btn.textContent = actionLabel;
        btn.style.cssText = `
            background: none;
            border: none;
            color: white;
            font-weight: 700;
            text-transform: uppercase;
            font-size: 13px;
            margin-left: 12px;
            cursor: pointer;
            text-decoration: underline;
            padding: 0;
            flex-shrink: 0;
        `;
        btn.addEventListener('click', () => {
            actionCallback();
            notification.remove();
            updateNotificationStack();
        });
        notification.appendChild(btn);
    }

    document.body.appendChild(notification);
    requestAnimationFrame(updateNotificationStack);

    setTimeout(() => {
        notification.dataset.exiting = 'true';
        notification.style.transition = 'opacity 0.3s ease-out';
        notification.style.opacity = '0';
        setTimeout(() => {
            notification.remove();
            updateNotificationStack();
        }, 300);
    }, duration);
}
